import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Reveal from '../../components/Reveal';
// 🔑 The empty-pool fallback names the licensed states from the licence mirror; it typed 'TX / FL' (Hannah, 2026-11-03).
import { LICENSED_STATE_CODES } from '../../constants/marketExpansion';
import { SAMPLE_TAPE_PUBLISHED, SAMPLE_TAPE_PATH } from '../../constants/sampleTape';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/**
 * DoorlyMarket — institutional investor landing page.
 *
 * Matches the visual rhythm of DoorlyProLanding.jsx exactly: same nav
 * shell, blue full-bleed hero, three-step / metric / feature card grids,
 * blue CTA banner, blue footer. Copy + sections are market-specific.
 * Brand colors only — #3a6ad9 / #acf44e / #ff082f / #111111 / #fffd5b.
 */
export default function MarketLanding() {
  const navigate = useNavigate();
  useEffect(() => { document.title = 'Doorly | Investor Portal'; }, []);

  // Reused throughout — Request Access maps to the existing investor
  // registration route mounted in App.js (`/market/register`).
  const REGISTER_PATH = '/market/register';

  // KPI cards on the Asset Characteristics section pull from the public
  // /api/market/landing-stats endpoint, which aggregates over the
  // market_pool_loans table. Empty pool → every numeric field is null
  // and renders "—" via the kpi() helper below.
  const [stats, setStats] = useState(null);
  useEffect(() => {
    axios.get(`${API_URL}/api/market/landing-stats`)
      .then(r => setStats(r.data || null))
      .catch(() => { /* leave stats null — every card renders "—" */ });
  }, []);

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const closeMobileMenu = () => setMobileMenuOpen(false);
  // Display helpers — all return "—" when the value is null/undefined.
  const fmt$        = (v) => (v == null ? '—' : '$' + Number(v).toLocaleString('en-US', { maximumFractionDigits: 0 }));
  const fmtPct      = (v, digits = 1) => (v == null ? '—' : Number(v).toFixed(digits) + '%');
  const fmtInt      = (v) => (v == null ? '—' : String(Number(v)));
  const fmtPoolSize = (v) => (!v || v === 0) ? '—' : v.toLocaleString('en-US') + ' loans';

  // Visual primitives shared across sections.
  const SectionEyebrow = ({ children }) => (
    <div className="inline-block text-xs font-bold uppercase mb-5"
      style={{ color: '#3a6ad9', letterSpacing: '0.1em', padding: '4px 14px', background: '#edf1fb', borderRadius: '20px', border: '1px solid #c5d3f5' }}>
      {children}
    </div>
  );
  const SectionHeadline = ({ children }) => (
    <h2 className="text-3xl sm:text-4xl font-bold text-black mb-4 leading-tight" style={{ letterSpacing: '-0.5px' }}>
      {children}
    </h2>
  );
  const Bullet = ({ children }) => (
    <div className="flex items-start gap-3">
      <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
        style={{ backgroundColor: '#acf44e' }}>
        <svg className="w-3 h-3" fill="none" stroke="#000" strokeWidth="3" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <span className="text-gray-700 text-sm leading-relaxed">{children}</span>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col bg-white">

      {/* ── Per-route meta / Open Graph ──────────────────────────────
          Helmet handles in-app SPA navigation (browser tab title +
          meta tag updates when a user clicks through to /market from
          another route). The static crawler-grade preview comes from
          frontend/public/index.html → frontend/build/market.html
          (rewritten at build time by scripts/build-market-html.js +
          served via the /market → /market.html Vercel rewrite), so
          OG scrapers like Facebook + LinkedIn that don't execute JS
          still see the right tags. */}
      <Helmet>
        <title>Doorly | Investor Portal</title>
        <meta name="description"            content="Institutional access to Doorly-originated AFM™ mortgage assets. Review curated pools, access due diligence materials, and participate in bid opportunities." />
        <meta property="og:title"            content="Doorly | Investor Portal" />
        <meta property="og:description"      content="Institutional access to Doorly-originated AFM™ mortgage assets. Review curated pools, access due diligence materials, and participate in bid opportunities." />
        <meta property="og:url"              content="https://app.godoorly.com/market" />
        <meta property="og:type"             content="website" />
        <meta property="og:site_name"        content="DoorlyMarket" />
        <meta name="twitter:card"            content="summary" />
        <meta name="twitter:title"           content="Doorly | Investor Portal" />
        <meta name="twitter:description"     content="Institutional access to Doorly-originated AFM™ mortgage assets. Review curated pools, access due diligence materials, and participate in bid opportunities." />
      </Helmet>

      {/* ── NAV ── */}
      {/* Wordmark text — preserved per spec. White nav, identical
          height + breakpoint behaviour to DoorlyProLanding's nav. */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between" style={{ height: '4.5rem' }}>
            <Link to="/market" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
              <img
                src="/doorly-market-logo.png"
                alt="Doorly Market"
                style={{ height: '40px', width: 'auto', display: 'block' }}
              />
            </Link>

            {/* Desktop nav — hidden on mobile, replaced by hamburger below */}
            <div className="hidden sm:flex items-center gap-2">
              <a href="https://www.godoorly.com" className="px-4 py-2 rounded-full text-sm font-medium text-black hover:text-doorly-blue hover:bg-gray-100 transition-colors duration-200">
                Home
              </a>
              <Link to="/market/login" className="px-4 py-1.5 rounded-full text-sm font-semibold text-black border border-gray-200 hover:bg-gray-50 transition-colors">
                Sign In
              </Link>
              <Link to={REGISTER_PATH} className="px-4 py-1.5 rounded-full text-sm font-bold transition-colors"
                style={{ backgroundColor: '#3a6ad9', color: '#fff' }}>
                Request Access
              </Link>
            </div>

            {/* Mobile hamburger — only visible below sm */}
            <button
              type="button"
              onClick={() => setMobileMenuOpen(o => !o)}
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileMenuOpen}
              className="sm:hidden p-2 -mr-2 rounded-md text-black hover:bg-gray-100 transition-colors"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                {mobileMenuOpen ? (
                  <>
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </>
                ) : (
                  <>
                    <line x1="3" y1="6" x2="21" y2="6" />
                    <line x1="3" y1="12" x2="21" y2="12" />
                    <line x1="3" y1="18" x2="21" y2="18" />
                  </>
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile dropdown panel */}
        {mobileMenuOpen && (
          <div className="sm:hidden border-t border-gray-100 bg-white shadow-sm">
            <div className="max-w-7xl mx-auto px-4 py-3 flex flex-col gap-2">
              <Link to="/market/login" onClick={closeMobileMenu}
                className="px-4 py-2.5 rounded-md text-sm font-semibold text-black border border-gray-200 hover:bg-gray-50 transition-colors text-center">
                Sign In
              </Link>
              <Link to={REGISTER_PATH} onClick={closeMobileMenu}
                className="px-4 py-2.5 rounded-md text-sm font-bold transition-colors text-center"
                style={{ backgroundColor: '#3a6ad9', color: '#fff' }}>
                Register
              </Link>
              <a href="https://www.godoorly.com" onClick={closeMobileMenu}
                className="px-4 py-2.5 rounded-md text-sm font-medium text-black hover:bg-gray-100 transition-colors text-center">
                Home
              </a>
            </div>
          </div>
        )}
      </nav>

      {/* ── HERO ── */}
      {/* Container widened from max-w-3xl → max-w-4xl so the headline
          ("Institutional Access to the") fits on a single line at every
          breakpoint instead of wrapping mid-phrase and orphaning "the"
          on its own line before the forced <br />. */}
      <section className="pt-28 sm:pt-36 pb-20 px-4 sm:px-6 lg:px-8" style={{ background: '#3a6ad9' }}>
        <div className="max-w-4xl mx-auto text-center">
          <Reveal>
            <div className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-full mb-6 uppercase tracking-wider"
              style={{
                backgroundColor: 'rgba(255,255,255,0.12)',
                border: '1px solid rgba(255,255,255,0.25)',
                color: '#ffffff',
              }}>
              <span className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: '#ffffff' }} />
              For Institutional Investors
            </div>
          </Reveal>

          {/* Forced 2-line title. Mobile pushed up to text-[28px] with
              tracking-tighter — sized as large as it can go while still
              keeping "Institutional Access to the" on a single line at
              modern phone widths. Scales up from sm: breakpoint. */}
          <Reveal delay={80}>
            <h1 className="text-[28px] sm:text-3xl md:text-4xl lg:text-5xl font-extrabold leading-tight tracking-tighter mb-6 text-white">
              Institutional Access to the<br />
              Doorly-Originated{' '}
              <span style={{ color: '#fffd5b' }}>AFM<sup style={{ fontSize: '0.5em', verticalAlign: 'super' }}>™</sup></span>
            </h1>
          </Reveal>

          <Reveal delay={160}>
            <p className="text-lg sm:text-xl leading-relaxed mb-4 max-w-2xl mx-auto" style={{ color: 'rgba(255,255,255,0.85)' }}>
              Built for investors seeking differentiated residential credit exposure.
            </p>
            <p className="text-base leading-relaxed mb-10 max-w-2xl mx-auto" style={{ color: 'rgba(255,255,255,0.7)' }}>
              DoorlyMarket gives institutional buyers access to curated pools of Doorly-originated mortgage assets backed by real homeowners, real collateral, and fully documented loan files.
            </p>
          </Reveal>

          <Reveal delay={240}>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to={REGISTER_PATH} className="doorly-cta doorly-cta-yellow text-base px-6 py-3 w-full sm:w-auto justify-center">
                <span className="doorly-cta-text">Request Access</span>
                <span className="doorly-cta-badge" aria-hidden="true">→</span>
              </Link>
              {SAMPLE_TAPE_PUBLISHED && (
              <Link to={SAMPLE_TAPE_PATH}
                className="doorly-cta text-base px-6 py-3 w-full sm:w-auto justify-center"
                style={{ backgroundColor: 'transparent', color: '#fff', border: '2px solid rgba(255,255,255,0.4)', cursor: 'pointer' }}>
                <span className="doorly-cta-text">View Sample Tape</span>
                <span className="doorly-cta-badge" aria-hidden="true" style={{ backgroundColor: 'rgba(255,255,255,0.2)', color: '#fff' }}>→</span>
              </Link>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── SECTION 1 — WHAT MAKES DOORLY DIFFERENT ── */}
      {/* Moved above the trust bar per design request — the differentiated-
          origination story is the first claim we make after the hero, then
          the infrastructure logos give it the institutional credibility
          backstop. */}
      <section className="bg-white py-20 px-4 sm:px-6 lg:px-8 border-t border-gray-100">
        <div className="max-w-4xl mx-auto">
          <Reveal>
            <div className="text-center mb-10">
              <SectionEyebrow>What Makes Doorly Different</SectionEyebrow>
              <SectionHeadline>
                <span style={{ color: '#111111' }}>This is not seller finance. This is </span>
                <span style={{ color: '#3a6ad9' }}>Doorly finance.</span>
              </SectionHeadline>
            </div>
          </Reveal>
          <Reveal delay={80}>
            <p className="text-gray-600 text-lg leading-relaxed mb-6 max-w-3xl mx-auto">
              Doorly combines institutional mortgage infrastructure with a modern purchase-and-resale model designed for today's homebuyer.
            </p>
          </Reveal>
          <Reveal delay={140}>
            <p className="text-gray-700 text-base font-semibold mb-4 max-w-3xl mx-auto">Every transaction includes:</p>
          </Reveal>
          <div className="space-y-3 max-w-2xl mx-auto">
            <Reveal delay={180}><Bullet>a same-day resale to our borrower</Bullet></Reveal>
            <Reveal delay={220}><Bullet>first-lien mortgages</Bullet></Reveal>
            <Reveal delay={260}><Bullet>fully documented underwriting</Bullet></Reveal>
            <Reveal delay={300}><Bullet>TRID-compliant disclosures</Bullet></Reveal>
            <Reveal delay={340}><Bullet>integrated income, identity, and asset verification</Bullet></Reveal>
          </div>
        </div>
      </section>

      {/* ── TRUST / INFRASTRUCTURE BAR ── */}
      {/* Vendor logos: <img> with onError fallback to a styled wordmark.
          Drop SVGs at /public/assets/vendors/<name>.svg to swap actual
          brand artwork in — until then the wordmark renders.
          Bigger spacing + grayscale for a polished "logo wall" feel. */}
      <section style={{ background: '#f4f6f9', padding: '56px 24px', textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <div style={{ display: 'inline-block', fontSize: '11px', fontWeight: '700', color: '#3a6ad9', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '28px', padding: '4px 14px', background: '#edf1fb', borderRadius: '20px', border: '1px solid #c5d3f5' }}>
            Powered by institutional-grade mortgage infrastructure
          </div>
          <div className="flex flex-wrap justify-center items-center gap-x-12 gap-y-8 mb-8">
            {[
              { key: 'atlas',          label: 'Atlas'           },
              // ReadyCheck removed 2026-10-15 (Jesse): an internal engine name,
              // and not a vendor — this strip is third-party infrastructure.
              { key: 'persona',        label: 'Persona'         },
              { key: 'inspectify',     label: 'Inspectify'      },
              { key: 'xactus',         label: 'Xactus'          },
              { key: 'stripe',         label: 'Stripe'          },
              { key: 'blueprint',      label: 'Blueprint'       },
            ].map((v, i) => (
              <Reveal key={v.key} delay={i * 60} y={10}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 40, transition: 'opacity 0.2s ease-out, transform 0.2s ease-out' }}
                  onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'scale(1.04)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.opacity = ''; e.currentTarget.style.transform = ''; }}>
                  <img
                    src={`/assets/vendors/${v.key}.svg`}
                    alt={v.label}
                    style={{ height: 32, width: 'auto', objectFit: 'contain', filter: 'grayscale(100%)', opacity: 0.85 }}
                    onError={(e) => {
                      // Hide the broken <img>, swap in the wordmark fallback
                      // sibling. Visible until the SVG asset is uploaded.
                      e.currentTarget.style.display = 'none';
                      const fb = e.currentTarget.nextSibling;
                      if (fb) fb.style.display = 'inline-block';
                    }}
                  />
                  <span style={{
                    display: 'none',
                    fontWeight: 800,
                    fontSize: 22,
                    letterSpacing: '-0.5px',
                    color: '#4b5563',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                  }}>
                    {v.label}
                  </span>
                </div>
              </Reveal>
            ))}
          </div>
          <p className="text-gray-600 text-sm sm:text-base leading-relaxed max-w-2xl mx-auto">
            Every Doorly loan file is originated, verified, and documented through industry-standard mortgage infrastructure and compliance systems.
          </p>
        </div>
      </section>

      {/* ── SECTION 2 — ASSET CHARACTERISTICS ── */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 border-t border-gray-100" style={{ backgroundColor: '#f9fafb' }}>
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Left: copy + bullet list */}
            <div>
              <Reveal>
                <SectionEyebrow>Asset Characteristics</SectionEyebrow>
                <SectionHeadline>Residential mortgage assets with institutional-grade documentation.</SectionHeadline>
                <p className="text-gray-600 text-sm font-semibold uppercase tracking-wider mb-4">Typical Characteristics</p>
              </Reveal>
              <div className="space-y-3">
                {[
                  'First-lien residential mortgages',
                  'Owner-occupied borrowers',
                  'Fully documented income and asset verification',
                  'TRID-compliant origination process',
                  'Integrated fraud, identity, and bank verification',
                  'Fully imbedded appraisal and inspection workflow',
                  'Non-agency / non-QM profile',
                ].map((item, i) => (
                  <Reveal key={i} delay={120 + i * 60} y={10}>
                    <Bullet>{item}</Bullet>
                  </Reveal>
                ))}
              </div>
            </div>

            {/* Right: 8 metric cards in 2×4 grid — sized to match the
                8-bullet list to its left so both columns end at the same
                vertical line. Alternating blue/neutral accent. */}
            <div className="grid grid-cols-2 gap-4">
              {[
                { label: 'Average FICO',             value: fmtInt(stats?.averageFico),                                accent: '#3a6ad9', bg: '#eff4ff', border: '#bfcff8' },
                { label: 'Average LTV',              value: fmtPct(stats?.averageLtv, 1),                              accent: '#000',    bg: '#f3f4f6', border: '#e5e7eb' },
                { label: 'Geographic Concentration', value: stats?.geographicConcentration || LICENSED_STATE_CODES.join(' / '),               accent: '#3a6ad9', bg: '#eff4ff', border: '#bfcff8' },
                { label: 'Delinquency Rate',         value: fmtPct(stats?.delinquencyRate, 1),                         accent: '#000',    bg: '#f3f4f6', border: '#e5e7eb' },
                { label: 'WA Coupon',                value: fmtPct(stats?.waCoupon, 2),                                accent: '#3a6ad9', bg: '#eff4ff', border: '#bfcff8' },
                { label: 'Pool Size',                value: fmtPoolSize(stats?.poolSize),                              accent: '#000',    bg: '#f3f4f6', border: '#e5e7eb' },
                { label: 'Average Note Rate',        value: fmtPct(stats?.averageNoteRate, 2),                         accent: '#3a6ad9', bg: '#eff4ff', border: '#bfcff8' },
                { label: 'Average Loan Size',        value: fmt$(stats?.averageLoanSize),                              accent: '#000',    bg: '#f3f4f6', border: '#e5e7eb' },
              ].map((m, i) => (
                <Reveal key={i} delay={i * 70} y={12}>
                  <div className="rounded-2xl p-5 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md"
                    style={{ backgroundColor: m.bg, border: `1px solid ${m.border}` }}>
                    <div className="text-gray-600 text-xs font-medium mb-2 uppercase" style={{ letterSpacing: '0.04em' }}>{m.label}</div>
                    <div className="text-2xl font-bold" style={{ color: m.accent }}>{m.value}</div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── SECTION 3 — TECHNOLOGY + COMPLIANCE STACK ── */}
      <section className="bg-white py-20 px-4 sm:px-6 lg:px-8 border-t border-gray-100">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <div className="text-center mb-12">
              <SectionEyebrow>Technology + Compliance Stack</SectionEyebrow>
              <SectionHeadline><span style={{ color: '#3a6ad9' }}>Built with compliance</span> and auditability at the core.</SectionHeadline>
              <p className="text-gray-600 text-lg leading-relaxed max-w-3xl mx-auto">
                Every transaction is processed through a modern mortgage infrastructure stack.
              </p>
            </div>
          </Reveal>

          {/* 6 feature cards — same shadow + radius + transition pattern as
              DoorlyPro's How-It-Works step cards. Auto-fit grid stays
              orderly at 1 / 2 / 3 columns at responsive breakpoints. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: 'Instant Identity Verification',
                desc: 'KYC and identity checks completed at application through integrated verification providers.',
              },
              {
                title: 'Income & Asset Verification',
                desc: 'Bank-connected income and asset verification with AI-powered documentation review.',
              },
              {
                title: 'Robust Compliance Engine',
                desc: 'TRID, ATR, and state-level compliance validated on every loan file before generation.',
              },
              {
                title: 'Integrated Title & Closings',
                desc: 'Title commitment, lien review, and closing coordination handled within the origination workflow.',
              },
              {
                title: 'Embedded Appraisal & Inspection Workflow',
                desc: 'Property condition and valuation confirmed through integrated appraisal and inspection ordering.',
              },
              {
                title: 'Homeownership Counseling',
                desc: (
                  <>
                    Borrowers enroll in Doorly SmartStart<sup style={{ fontSize: '0.6em', verticalAlign: 'super' }}>™</sup>, our embedded homeownership counseling program designed to support long-term success and reduce post-close default risk.
                  </>
                ),
              },
            ].map((f, i) => (
              <Reveal key={i} delay={i * 80}>
                <div className="bg-white rounded-2xl p-8 shadow-md hover:shadow-xl hover:-translate-y-1 transition-all duration-300 border border-gray-100 h-full">
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-5"
                    style={{ backgroundColor: '#edf1fb', color: '#3a6ad9' }}>
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-bold text-black mb-2">{f.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{f.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── SECTION 4 — DUE DILIGENCE ACCESS ── */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 border-t border-gray-100" style={{ backgroundColor: '#f9fafb' }}>
        <div className="max-w-4xl mx-auto text-center">
          <Reveal>
            <SectionEyebrow>Due Diligence Access</SectionEyebrow>
            <SectionHeadline>Institutional diligence materials available upon approval.</SectionHeadline>
            <p className="text-gray-600 text-lg leading-relaxed mb-10 max-w-2xl mx-auto">
              Approved buyers receive access to:
            </p>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-left max-w-2xl mx-auto mb-10">
            {[
              'Bid tapes',
              'Loan-level data',
              'Servicing data',
              'Collateral documentation',
              'Loan packages',
              'Underwriting guidelines',
            ].map((item, i) => (
              <Reveal key={i} delay={i * 70} y={10}>
                <div className="flex items-start gap-3 rounded-xl p-4 bg-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-sm"
                  style={{ border: '1px solid #e5e7eb' }}>
                  <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{ backgroundColor: '#3a6ad9', color: '#fff' }}>
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-gray-700 text-sm leading-relaxed font-medium">{item}</span>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={500}>
            <Link to={REGISTER_PATH} className="doorly-cta text-base px-6 py-3 inline-flex">
              <span className="doorly-cta-text">Request Investor Access</span>
              <span className="doorly-cta-badge" aria-hidden="true">→</span>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ── SECTION 5 — WHO WE WORK WITH ── */}
      <section className="bg-white py-20 px-4 sm:px-6 lg:px-8 border-t border-gray-100">
        <div className="max-w-4xl mx-auto text-center">
          <Reveal>
            <SectionEyebrow>Who We Work With</SectionEyebrow>
            <SectionHeadline>Built for institutional residential credit buyers.</SectionHeadline>
            <p className="text-gray-600 text-sm font-semibold uppercase tracking-wider mb-10">Ideal counterparties</p>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {[
              'Whole loan buyers',
              'Credit funds',
              'Family offices',
              'Structured credit investors',
              'Private debt platforms',
              'Specialty finance groups',
            ].map((item, i) => (
              <Reveal key={i} delay={i * 70}>
                <div className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl hover:-translate-y-1 transition-all duration-300 border border-gray-100 text-left h-full">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-8 rounded-sm flex-shrink-0" style={{ backgroundColor: '#3a6ad9' }} />
                    <span className="text-base font-bold text-black">{item}</span>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA BANNER ── */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 text-center" style={{ background: '#3a6ad9' }}>
        <div className="max-w-2xl mx-auto">
          <Reveal>
            <div style={{ width: '48px', height: '4px', background: '#fffd5b', borderRadius: '2px', margin: '0 auto 24px' }} />
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 leading-tight">
              Ready to access institutional offerings?
            </h2>
            <p className="text-lg leading-relaxed mb-10" style={{ color: 'rgba(255,255,255,0.7)' }}>
              Submit your firm for review. Approved counterparties gain access to bid tapes and the full diligence room.
            </p>
          </Reveal>
          <Reveal delay={150}>
            <Link to={REGISTER_PATH} className="doorly-cta doorly-cta-yellow text-base px-6 py-3 inline-flex">
              <span className="doorly-cta-text">Request Investor Access</span>
              <span className="doorly-cta-badge" aria-hidden="true">→</span>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="px-5 sm:px-10 py-6 sm:py-7" style={{
        background: '#3a6ad9',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        borderTop: '1px solid rgba(255,255,255,0.08)',
      }}>
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.35)' }}>
            Go Doorly, LLC | NMLS #2764749 | Equal Housing Opportunity
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-2 sm:gap-x-6">
            {[
              { label: 'Sign In',                  path: '/market/login' },
              { label: 'Request Access',           path: REGISTER_PATH },
              { label: 'Investor Dashboard',       path: '/market/dashboard' },
              { label: 'Support',                  path: 'mailto:investors@godoorly.com' },
            ].map(link => (
              <span
                key={link.label}
                onClick={() => link.path.startsWith('mailto') ? (window.location.href = link.path) : navigate(link.path)}
                style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', transition: 'color 0.15s' }}
                onMouseEnter={e => e.target.style.color = '#fffd5b'}
                onMouseLeave={e => e.target.style.color = 'rgba(255,255,255,0.5)'}
              >
                {link.label}
              </span>
            ))}
          </div>
        </div>

        {/* Disclaimer — institutional / accredited counterparties only */}
        <div style={{
          fontSize: '11px',
          color: 'rgba(255,255,255,0.35)',
          lineHeight: 1.6,
          borderTop: '1px solid rgba(255,255,255,0.08)',
          paddingTop: '14px',
          marginTop: '4px',
        }}>
          DoorlyMarket is intended solely for institutional and accredited counterparties. Access to loan-level information and offerings is subject to approval and confidentiality requirements.
        </div>
      </footer>
    </div>
  );
}
